import { useEffect, useMemo, useState, useCallback } from 'react';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Task, TaskStatusDef, DEFAULT_TASK_STATUSES } from '../types/task';

/**
 * Szervezet szintű egyedi feladat-státuszok (organizations/{id}.taskStatuses).
 * Ha nincs beállítva, a 4 alapértelmezett státusz érvényes.
 */
export const useTaskStatuses = () => {
  const { user } = useAuth();
  const orgId = (user as any)?.organizationId as string | undefined;
  const [custom, setCustom] = useState<TaskStatusDef[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) { setCustom(null); setLoading(false); return; }
    const unsub = onSnapshot(doc(db, 'organizations', orgId), (snap) => {
      const arr = snap.exists() ? (snap.data().taskStatuses as TaskStatusDef[] | undefined) : undefined;
      setCustom(Array.isArray(arr) && arr.length > 0 ? [...arr].sort((a, b) => a.order - b.order) : null);
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, [orgId]);

  const statuses = useMemo(() => custom || DEFAULT_TASK_STATUSES, [custom]);

  const save = useCallback(async (next: TaskStatusDef[]) => {
    if (!orgId) throw new Error('Nincs aktív szervezet');
    const cleaned = next.map((s, i) => ({ ...s, order: i }));
    await updateDoc(doc(db, 'organizations', orgId), { taskStatuses: cleaned });
  }, [orgId]);

  /** A feladat aktuális státusz-definíciója (egyedi vagy kategória szerinti fallback). */
  const statusOf = useCallback((task: Pick<Task, 'status' | 'statusId'>): TaskStatusDef => {
    if (task.statusId) {
      const s = statuses.find(x => x.id === task.statusId);
      if (s) return s;
    }
    return statuses.find(x => x.category === task.status) || statuses[0];
  }, [statuses]);

  return { statuses, isCustom: !!custom, loading, save, statusOf };
};

export default useTaskStatuses;
